import { useState } from 'react'
import { Share2, Copy, Check, Link, Users, Calendar, Trophy, Settings, ExternalLink } from 'lucide-react'
import { useUser } from '@clerk/clerk-react'
import { createShareableLink, createCompetitionTrip, createGroup } from '@/lib/firestore'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { Switch } from './switch'
import { Textarea } from './textarea'

interface ShareDialogProps {
  isOpen: boolean
  onClose: () => void
  type: 'group' | 'create-trip' | 'all-time'
  currentTripId?: string
  onTripCreated?: (tripId: string) => void
}

export function ShareDialog({ isOpen, onClose, type, currentTripId, onTripCreated }: ShareDialogProps) {
  const { user } = useUser()
  const { toast } = useToast()
  
  // Sharing states
  const [shareToken, setShareToken] = useState<string>('')
  const [copied, setCopied] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  
  // Group/Trip creation states
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [tripName, setTripName] = useState('')
  const [tripDescription, setTripDescription] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [allowLateJoins, setAllowLateJoins] = useState(true)
  const [allowNewMembers, setAllowNewMembers] = useState(true)
  const [trackLocation, setTrackLocation] = useState(false)
  const [expirationDays, setExpirationDays] = useState<number | undefined>(undefined)

  const handleCreateGroup = async () => {
    if (!user?.id || !groupName.trim()) return

    try {
      setIsCreating(true)
      
      const groupId = await createGroup(
        groupName.trim(),
        groupDescription.trim() || undefined,
        user.id,
        user.fullName || user.firstName || 'Anonymous',
        {
          isPublic,
          allowNewMembers,
          leaderboardType: 'all-time'
        }
      )

      // Create share token for the group
      const token = await createShareableLink('group', user.id, groupId, expirationDays)
      setShareToken(token)
      
      toast({
        title: "Group Created!",
        description: `${groupName} is ready! Share the link so friends can join your leaderboard.`,
        duration: 3000,
      })
    } catch (error) {
      console.error('Error creating group:', error)
      toast({
        title: "Error",
        description: "Failed to create group. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateAllTimeShare = async () => {
    if (!user?.id) return

    try {
      setIsCreating(true)
      const token = await createShareableLink('all-time', user.id, undefined, expirationDays)
      setShareToken(token)
      
      toast({
        title: "Share Link Created!",
        description: "Your all-time progress can now be shared with others.",
        duration: 3000,
      })
    } catch (error) {
      console.error('Error creating share link:', error)
      toast({
        title: "Error",
        description: "Failed to create share link. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateTrip = async () => {
    if (!user?.id || !tripName.trim()) return

    try {
      setIsCreating(true)
      
      const tripId = await createCompetitionTrip({
        name: tripName.trim(),
        description: tripDescription.trim() || undefined,
        creatorId: user.id,
        creatorName: user.fullName || user.firstName || 'Anonymous',
        participants: [user.id],
        isActive: true,
        isPublic,
        settings: {
          allowLateJoins,
          trackLocation,
          autoEnd: expirationDays ? new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000) : undefined
        }
      })

      // Create share token for the trip
      const token = await createShareableLink('trip', user.id, tripId, expirationDays)
      setShareToken(token)
      
      toast({
        title: "Trip Created!",
        description: `${tripName} is ready for competition. Share the link with friends!`,
        duration: 3000,
      })

      if (onTripCreated) {
        onTripCreated(tripId)
      }
    } catch (error) {
      console.error('Error creating trip:', error)
      toast({
        title: "Error",
        description: "Failed to create trip. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setIsCreating(false)
    }
  }

  const copyToClipboard = async () => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`
    
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      
      toast({
        title: "Link Copied!",
        description: "Share link has been copied to your clipboard.",
        duration: 2000,
      })
      
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast({
        title: "Failed to Copy",
        description: "Please copy the link manually.",
        variant: "destructive",
        duration: 3000,
      })
    }
  }

  const resetDialog = () => {
    setShareToken('')
    setCopied(false)
    setGroupName('')
    setGroupDescription('')
    setTripName('')
    setTripDescription('')
    setIsPublic(true)
    setAllowLateJoins(true)
    setAllowNewMembers(true)
    setTrackLocation(false)
    setExpirationDays(undefined)
    setIsCreating(false)
  }

  const handleClose = () => {
    resetDialog()
    onClose()
  }

  const getDialogTitle = () => {
    if (shareToken) {
      return type === 'group' ? 'Group Created!' : 
             type === 'all-time' ? 'Share Link Created!' : 'Trip Created!'
    }
    return type === 'group' ? 'Create Leaderboard Group' : 
           type === 'all-time' ? 'Share All-Time Progress' : 'Create Competition Trip'
  }

  const getDialogDescription = () => {
    if (shareToken) {
      return type === 'group' 
        ? 'Share this link with friends so they can join your group and compete on the leaderboard.'
        : type === 'all-time'
        ? 'Share this link with others to show your all-time team spotting progress.'
        : 'Share this link with friends to invite them to join your competition trip.'
    }
    return type === 'group'
      ? 'Create a group where friends can join and compete to see who spots the most teams.'
      : type === 'all-time'
      ? 'Create a shareable link to show off your team spotting achievements.'
      : 'Create a new trip that friends can join to compete in team spotting.'
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {shareToken ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : type === 'group' ? (
              <Trophy className="h-5 w-5 text-[#42a5f5]" />
            ) : type === 'all-time' ? (
              <Share2 className="h-5 w-5 text-[#42a5f5]" />
            ) : (
              <Users className="h-5 w-5 text-[#42a5f5]" />
            )}
            <span>{getDialogTitle()}</span>
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {shareToken ? (
            /* Share Link Created */
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Share Link
                </Label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={`${window.location.origin}/shared/${shareToken}`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Link className="h-4 w-4" />
                <span>
                  {type === 'group' 
                    ? 'Anyone with this link can join your group and compete on the leaderboard.'
                    : type === 'all-time'
                    ? 'Anyone with this link can view your team spotting progress.'
                    : 'Anyone with this link can join your competition trip.'
                  }
                </span>
              </div>

              <div className="flex space-x-2">
                <Button onClick={handleClose} className="flex-1">
                  Done
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(`/shared/${shareToken}`, '_blank')}
                  className="flex items-center space-x-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Preview</span>
                </Button>
              </div>
            </div>
          ) : type === 'group' ? (
            /* Create Group */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="groupName">Group Name *</Label>
                <Input
                  id="groupName"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g., Friends Leaderboard, Work Team Challenge"
                  maxLength={50}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="groupDescription">Description (Optional)</Label>
                <Textarea
                  id="groupDescription"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Add details about your group..."
                  maxLength={200}
                  rows={3}
                />
              </div>

              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Group Settings</span>
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Public Group</Label>
                      <p className="text-xs text-gray-500">Show group in public listings</p>
                    </div>
                    <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Allow New Members</Label>
                      <p className="text-xs text-gray-500">Let people join with the link</p>
                    </div>
                    <Switch checked={allowNewMembers} onCheckedChange={setAllowNewMembers} />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiration">Link Expiration (Optional)</Label>
                <select
                  id="expiration"
                  value={expirationDays || ''}
                  onChange={(e) => setExpirationDays(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#42a5f5] focus:border-[#42a5f5]"
                >
                  <option value="">Never expires</option>
                  <option value="7">1 week</option>
                  <option value="30">1 month</option>
                  <option value="90">3 months</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <Button
                onClick={handleCreateGroup}
                disabled={isCreating || !groupName.trim()}
                className="w-full"
              >
                {isCreating ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Creating Group...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Trophy className="h-4 w-4" />
                    <span>Create Leaderboard Group</span>
                  </div>
                )}
              </Button>
            </div>
          ) : type === 'all-time' ? (
            /* Create All-Time Share */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="expiration">Link Expiration (Optional)</Label>
                <select
                  id="expiration"
                  value={expirationDays || ''}
                  onChange={(e) => setExpirationDays(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#42a5f5] focus:border-[#42a5f5]"
                >
                  <option value="">Never expires</option>
                  <option value="1">1 day</option>
                  <option value="7">1 week</option>
                  <option value="30">1 month</option>
                  <option value="90">3 months</option>
                </select>
              </div>

              <Button
                onClick={handleCreateAllTimeShare}
                disabled={isCreating}
                className="w-full"
              >
                {isCreating ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Creating Link...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Share2 className="h-4 w-4" />
                    <span>Create Share Link</span>
                  </div>
                )}
              </Button>
            </div>
          ) : (
            /* Create Competition Trip */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tripName">Trip Name *</Label>
                <Input
                  id="tripName"
                  value={tripName}
                  onChange={(e) => setTripName(e.target.value)}
                  placeholder="e.g., Summer Road Trip 2024"
                  maxLength={50}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tripDescription">Description (Optional)</Label>
                <Textarea
                  id="tripDescription"
                  value={tripDescription}
                  onChange={(e) => setTripDescription(e.target.value)}
                  placeholder="Add details about your trip..."
                  maxLength={200}
                  rows={3}
                />
              </div>

              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Trip Settings</span>
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Public Trip</Label>
                      <p className="text-xs text-gray-500">Allow anyone with the link to join</p>
                    </div>
                    <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Allow Late Joins</Label>
                      <p className="text-xs text-gray-500">Let people join after the trip starts</p>
                    </div>
                    <Switch checked={allowLateJoins} onCheckedChange={setAllowLateJoins} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Track Locations</Label>
                      <p className="text-xs text-gray-500">Save where teams were spotted</p>
                    </div>
                    <Switch checked={trackLocation} onCheckedChange={setTrackLocation} />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tripExpiration">Auto-End Trip (Optional)</Label>
                <select
                  id="tripExpiration"
                  value={expirationDays || ''}
                  onChange={(e) => setExpirationDays(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#42a5f5] focus:border-[#42a5f5]"
                >
                  <option value="">Never ends</option>
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="7">1 week</option>
                  <option value="14">2 weeks</option>
                  <option value="30">1 month</option>
                </select>
              </div>

              <Button
                onClick={handleCreateTrip}
                disabled={isCreating || !tripName.trim()}
                className="w-full"
              >
                {isCreating ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    <span>Creating Trip...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>Create Competition Trip</span>
                  </div>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}